import { Async, F1, F2, TypedValue } from '@client/new/helpers/types';
import {
  ContentMap,
  ContentRootTree,
  Content,
  ExtrasContent,
  ContentPDF,
} from './extras.domain';
import * as g from 'generated/graphql';
import { useAsyncQuery } from '@client/new/helpers/gql';
import { toExtrasContent, toProfileData } from './extras.mappers';
import { _noop } from '@client/new/helpers/general';
import { ProfileData } from '@design-system/components/mobile-preview/types';
import { useMutationLog } from './useMutationLog';
import { BusinessModel } from '@playbook-technologies/common-enums';

type ExtrasModelParams = {
  creatorId: number;
};
export type CreatePdfInput2 = TypedValue<'ContentPDF', g.CreatePdfInput>; //TODO:cleanup

export type CreateContentInput =
  | CreatePdfInput2
  | TypedValue<'ContentImage', g.CreateImageInput>
  | TypedValue<'ContentSlides', g.CreateSlidesInput>
  | TypedValue<'ContentLegacyGuide', g.CreateLegacyGuideInput>
  | TypedValue<'ContentLink', g.CreateLinkInput>
  | TypedValue<'ContentVideo', g.CreateVideoInput>
  | TypedValue<'ContentTab', g.CreateTabInput>;

export type UpdateContentInput =
  | TypedValue<'ContentPDF', g.UpdatePdfInput>
  | TypedValue<'ContentSlides', g.UpdateSlidesInput>
  | TypedValue<'ContentLegacyGuide', g.UpdateLegacyGuideInput>
  | TypedValue<'ContentLink', g.UpdateLinkInput>
  | TypedValue<'ContentVideo', g.UpdateVideoInput>
  | TypedValue<'ContentTab', g.UpdateTabInput>;

type CreateContentParams = {
  input: CreateContentInput;
  file?: File | null;
  onProgress?: (n: number) => void;
};

export type ExtrasModel = {
  profile: Async<ProfileData>;
  data: Async<ExtrasContent>;
  isBundleCreator: boolean;
  reorder: F1<ExtrasContent['contentIds'], Promise<unknown>>;
  create: F1<CreateContentParams, Promise<ExtrasContent>>;
  update: F2<string, UpdateContentInput, Promise<ExtrasContent>>;
  delete: F1<Content, Promise<unknown>>;
  createPDF: (p: {
    input: CreatePdfInput2['value'];
    file: File;
    onProgress?: (n: number) => void;
  }) => Promise<ContentPDF['value']>;
  updatePDF: (p: {
    itemId: string;
    input: g.UpdatePdfInput;
    file: File;
    onProgress?: (n: number) => void;
  }) => Promise<ContentPDF['value']>;
  sellAsSubscription: (p: {
    itemId: string;
    type: string;
  }) => Promise<ExtrasContent>;
  sellAsAnyPurchase: (p: {
    itemId: string;
    type: string;
  }) => Promise<ExtrasContent>;
  sellOnlyInBundle: (p: {
    itemId: string;
    type: string;
  }) => Promise<ExtrasContent>;
  archiveContent: (p: {
    itemId: string;
    type: string;
  }) => Promise<ExtrasContent>;
  unarchiveContent: (p: {
    itemId: string;
    type: string;
  }) => Promise<ExtrasContent>;
  isMutationRunning: boolean;
  isItemMutating: (contentId: string) => boolean;
};

export const useExtrasModel = (p: ExtrasModelParams): ExtrasModel => {
  const creatorId = String(p.creatorId);
  const { data, refetch } = useAsyncQuery(
    g.useGetContentQuery({ variables: { creatorId } }),
    'content',
    toExtrasContent,
    { contentIds: { id: '' }, contentMap: {}, childrenMap: {} },
  );

  // Get profile with businessModel
  const profileQueryResult = g.useGetProfileQuery({
    variables: { id: Number(creatorId) },
  });
  const { data: profile } = useAsyncQuery(
    profileQueryResult,
    'creator',
    toProfileData,
  );

  // Calculate if creator is bundled
  const isBundleCreator =
    profileQueryResult.data?.creator?.businessModel === BusinessModel.Bundle;

  const [reorderContentMutation] = g.useReorderContentMutation();
  const createMutation = useCreate(creatorId);
  const updateMutation = useUpdate(creatorId);
  const [deleteMutation] = g.useDeleteContentMutation();
  const [createPDFMutation] = g.useCreatePdfMutation();
  const [updatePDFMutation] = g.useUpdatePdfMutation();
  const [sellAsSubscriptionMutation] = g.useSellAsSubscriptionMutation();
  const [sellAsAnyPurchaseMutation] = g.useSellAsAnyPurchaseMutation();
  const [archiveContentMutation] = g.useArchiveContentMutation();
  const [unarchiveContentMutation] = g.useUnarchiveContentMutation();
  const logger = useMutationLog();

  const withRefetchLogger = async <T = unknown>(
    p: { id: string; name: string; payload: Record<string, unknown> },
    cb: () => Promise<T>,
  ) => {
    logger.add({
      name: p.name,
      payload: { ...p.payload, id: p.id },
    });
    try {
      const res = await cb();
      // Refetch first to get fresh data, then mark mutation as complete
      const refetchResult = await refetch();
      logger.update(p.id, { type: 'fetched', value: res });
      return refetchResult;
    } catch (err) {
      logger.update(p.id, {
        type: 'fetch-error',
        value: err instanceof Error ? err.message : 'Unknown error',
      });
      throw err;
    }
  };

  const reorder: ExtrasModel['reorder'] = async (contentIds) => {
    if (!contentIds?.id)
      return Promise.reject(new Error('Invalid content IDs'));
    const id = contentIds.id || 'empty';
    return withRefetchLogger({ name: 'reorder', id, payload: contentIds }, () =>
      reorderContentMutation({ variables: { creatorId, contentIds } }),
    );
  };

  const createContent: ExtrasModel['create'] = async (p: {
    input: CreateContentInput;
    file?: File | null;
    onProgress?: (n: number) => void;
  }) => {
    if (data.type !== 'fetched')
      return Promise.reject(new Error('Data not fetched'));
    const id = p.input.value.id || '';
    return withRefetchLogger(
      {
        name: 'create' + p.input.type,
        payload: { ...p.input.value },
        id,
      },
      () =>
        createMutation({
          data: p.input,
          tree: data.value.contentIds,
          contentMap: data.value.contentMap,
          file: p.file,
          onProgress: p.onProgress,
        }),
    );
  };

  const updateContent: ExtrasModel['update'] = async (itemId, ct) => {
    if (data.type !== 'fetched')
      return Promise.reject(new Error('Data not fetched'));
    return withRefetchLogger(
      {
        name: 'update' + ct.type,
        payload: ct.value,
        id: itemId,
      },
      () => updateMutation(itemId, ct),
    );
  };

  const deleteContent: ExtrasModel['delete'] = async (ct) => {
    return withRefetchLogger(
      {
        name: 'delete' + ct.type,
        payload: ct.value,
        id: ct.value.id,
      },
      () => deleteMutation({ variables: { creatorId, itemId: ct.value.id } }),
    );
  };

  const createPDF: ExtrasModel['createPDF'] = async (p) => {
    const id = p.input.id || '';
    const refetchRes = await withRefetchLogger(
      { id, payload: p.input, name: 'createPDF' },
      () =>
        createPDFMutation({
          variables: {
            creatorId: creatorId,
            input: p.input,
            file: p.file,
          },
          context: {
            onUploadProgress: p.onProgress,
          },
        }),
    );
    const res = refetchRes.contentMap[id]?.value as ContentPDF['value'];
    if (!res) throw new Error('Cannot create PDF');
    return res;
  };

  const updatePDF: ExtrasModel['updatePDF'] = async (p) => {
    const refetchRes = await withRefetchLogger(
      { id: p.itemId || '', payload: p.input, name: 'updatePDF' },
      () =>
        updatePDFMutation({
          variables: {
            itemId: p.itemId,
            creatorId: creatorId,
            input: p.input,
            file: p.file,
          },
          context: {
            onUploadProgress: p.onProgress,
          },
        }),
    );
    return refetchRes.contentMap[p.itemId].value as ContentPDF['value'];
  };

  const sellAsSubscription: ExtrasModel['sellAsSubscription'] = async (p) => {
    return withRefetchLogger(
      { id: p.itemId, payload: p, name: 'sellAsSubscription' },
      () =>
        sellAsSubscriptionMutation({
          variables: {
            creatorId,
            itemId: p.itemId,
            type: p.type,
            accessMethod: g.ContentAccessMethod.SubscriptionAndBundles,
          },
        }),
    );
  };

  const sellAsAnyPurchase: ExtrasModel['sellAsAnyPurchase'] = async (p) => {
    return withRefetchLogger(
      { id: p.itemId, payload: p, name: 'sellAsAnyPurchase' },
      () =>
        sellAsAnyPurchaseMutation({
          variables: {
            creatorId,
            itemId: p.itemId,
            type: p.type,
            accessMethod: g.ContentAccessMethod.Default,
          },
        }),
    );
  };

  const sellOnlyInBundle: ExtrasModel['sellOnlyInBundle'] = async (p) => {
    return withRefetchLogger(
      { id: p.itemId, payload: p, name: 'sellOnlyInBundle' },
      () =>
        sellAsSubscriptionMutation({
          variables: {
            creatorId,
            itemId: p.itemId,
            type: p.type,
            accessMethod: g.ContentAccessMethod.BundlesOnly,
          },
        }),
    );
  };

  const archiveContent: ExtrasModel['archiveContent'] = async (p) => {
    return withRefetchLogger(
      { id: p.itemId, payload: p, name: 'archiveContent' },
      () =>
        archiveContentMutation({
          variables: {
            creatorId,
            itemId: p.itemId,
            type: p.type,
          },
        }),
    );
  };

  const unarchiveContent: ExtrasModel['unarchiveContent'] = async (p) => {
    return withRefetchLogger(
      { id: p.itemId, payload: p, name: 'unarchiveContent' },
      () =>
        unarchiveContentMutation({
          variables: {
            creatorId,
            itemId: p.itemId,
            type: p.type,
          },
        }),
    );
  };

  return {
    profile,
    createPDF,
    updatePDF,
    data,
    isBundleCreator,
    reorder,
    create: createContent,
    update: updateContent,
    delete: deleteContent,
    sellAsSubscription,
    sellAsAnyPurchase,
    sellOnlyInBundle,
    archiveContent,
    unarchiveContent,
    isMutationRunning: logger.isMutationRunning,
    isItemMutating: logger.isItemMutating,
  };
};

const useCreate = (creatorId: string) => {
  const [createLink] = g.useCreateLinkMutation();
  const [createTab] = g.useCreateTabMutation({});
  const [createLegacyGuide] = g.useCreateLegacyGuideMutation();
  const [createSlides] = g.useCreateSlidesMutation();
  const [createImage] = g.useCreateImageMutation();
  const [createVideo] = g.useCreateVideoMutation();
  const [createPDF] = g.useCreatePdfMutation();

  const createMutation = (p: {
    input: CreateContentInput;
    file?: File | null;
    onProgress?: (n: number) => void;
  }) => {
    const { input: d, file, onProgress } = p;
    const vars = <T>() => ({
      variables: { creatorId, input: { ...d.value } as T },
      context: {
        onUploadProgress: onProgress,
      },
    });

    switch (d.type) {
      case 'ContentLink':
        return createLink(vars<g.CreateLinkInput>()).then(
          (res) => res.data?.createLink,
        );

      case 'ContentTab':
        return createTab(vars<g.CreateTabInput>()).then(
          (res) => res.data?.createTab,
        );

      case 'ContentLegacyGuide':
        return createLegacyGuide(vars<g.CreateLegacyGuideInput>()).then(
          (res) => res.data?.createLegacyGuide,
        );

      case 'ContentSlides':
        return createSlides(vars<g.CreateSlidesInput>()).then(
          (res) => res.data?.createSlides,
        );

      case 'ContentVideo':
        return createVideo(vars<g.CreateVideoInput>()).then(
          (res) => res.data?.createVideo,
        );

      case 'ContentPDF':
        return createPDF({
          variables: {
            creatorId,
            input: d.value,
            file,
          },
          context: { onUploadProgress: onProgress },
        }).then((res) => res.data?.createPDF);

      case 'ContentImage':
        return createImage({
          variables: {
            creatorId,
            input: d.value,
            file,
          },
          context: { onUploadProgress: onProgress },
        }).then((res) => res.data?.createImage);
    }
  };

  return async (p: CreateParams) => {
    const res = await createMutation({
      input: p.data,
      file: p.file,
      onProgress: p.onProgress,
    });
    if (!res?.id) throw new Error(`Failed to create content ${p.data.type}`);
  };
};

type CreateParams = {
  data: CreateContentInput;
  tree: ContentRootTree;
  contentMap: ContentMap;
  file?: File | null;
  onProgress?: (n: number) => void;
};

const useUpdate = (creatorId: string) => {
  const [updateTab] = g.useUpdateTabMutation();
  const [updateLink] = g.useUpdateLinkMutation();
  const [updateLegacyGuide] = g.useUpdateLegacyGuideMutation();
  const [updateSlides] = g.useUpdateSlidesMutation();
  const [updatePDF] = g.useUpdatePdfMutation();
  const [updateVideo] = g.useUpdateVideoMutation();

  return async (itemId: string, d: UpdateContentInput) => {
    const { ...input } = d.value;
    const vars = () => ({ variables: { creatorId, itemId, input } });

    switch (d.type) {
      case 'ContentLink':
        return updateLink(vars());
      case 'ContentTab':
        return updateTab(vars());
      case 'ContentLegacyGuide':
        return updateLegacyGuide(vars());
      case 'ContentSlides':
        return updateSlides({
          variables: { creatorId, itemId, input: input as g.UpdateSlidesInput },
        }).then((res) => res.data?.updateSlides);
      case 'ContentPDF':
        return updatePDF({
          variables: { creatorId, itemId, input: input as g.UpdatePdfInput },
          context: {
            onUploadProgress: () => _noop, // Placeholder for progress callback),
          },
        }).then((res) => res.data?.updatePDF);
      case 'ContentVideo':
        return updateVideo({
          variables: { creatorId, itemId, input: input as g.UpdateVideoInput },
        }).then((res) => res.data?.updateVideo);
    }
  };
};
